import { Button } from "../../ui/button";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../ui/form";
import { Input } from "../../ui/input";
import { Alert, AlertTitle } from "../../ui/alert";
import { ExclamationTriangleIcon } from "@radix-ui/react-icons";
import { AnthropicChatSettings } from "@/types/ChatSettings";
import { forwardRef, useImperativeHandle, useEffect } from "react";
import { anthropicChatProviderValidator } from "@shared/validators/admin/providers/chat-provider-validator";
import type { ZodType } from "zod";

type Props = {
    data?: AnthropicChatSettings;
    initialData?: any;
    onDataChange?: (data: any) => void;
    readOnly?: boolean;
    error?: string;
    submitButton?: React.ReactNode;
    onSubmit?: (data: any) => void;
    hideSubmitButton?: boolean;
};

const AnthropicChatSettingsForm = forwardRef(
    (
        { data, initialData = {}, onDataChange, submitButton, readOnly, hideSubmitButton, error, onSubmit }: Props,
        ref
    ) => {
        const form = useForm<AnthropicChatSettings>({
            resolver: zodResolver(anthropicChatProviderValidator as ZodType<AnthropicChatSettings>),
            defaultValues: {
                ANTHROPIC_API_KEY: initialData?.ANTHROPIC_API_KEY || data?.ANTHROPIC_API_KEY || "",
                ANTHROPIC_MODEL: initialData?.ANTHROPIC_MODEL || data?.ANTHROPIC_MODEL || "claude-sonnet-4-20250514",
                ANTHROPIC_BASE_URL: initialData?.ANTHROPIC_BASE_URL || data?.ANTHROPIC_BASE_URL || "https://api.anthropic.com/v1/",
                ANTHROPIC_TEMPERATURE: initialData?.ANTHROPIC_TEMPERATURE || data?.ANTHROPIC_TEMPERATURE || 0.7,
                ANTHROPIC_MAX_TOKENS: initialData?.ANTHROPIC_MAX_TOKENS || data?.ANTHROPIC_MAX_TOKENS || 4096,
            },
        });

        useEffect(() => {
            const subscription = form.watch((value) => {
                if (onDataChange) {
                    onDataChange(value);
                }
            });
            return () => subscription.unsubscribe();
        }, [form, onDataChange]);

        useImperativeHandle(ref, () => ({
            validateForm: async () => {
                const result = await form.trigger();
                if (!result) {
                    const errors = form.formState.errors;
                    const firstError = Object.values(errors)[0];
                    if (firstError?.message) {
                        console.error("Anthropic Chat validation error:", firstError.message);
                    }
                }
                return result;
            },
            getFormData: () => form.getValues(),
            submitForm: () => {
                if (onSubmit) {
                    form.handleSubmit(onSubmit)();
                }
            },
        }));

        return (
            <Form {...form}>
                <form onSubmit={form.handleSubmit((data) => onSubmit && onSubmit(data))}>
                    <div>
                        {error && (
                            <Alert variant="destructive" className="mb-2 dark:bg-red-800 dark:text-white">
                                <div className="flex items-center gap-2">
                                    <ExclamationTriangleIcon className="h-4 w-4" />
                                    <AlertTitle className="mb-0">{error}</AlertTitle>
                                </div>
                            </Alert>
                        )}
                        <div className="space-y-3">
                            <FormField
                                control={form.control}
                                name="ANTHROPIC_API_KEY"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel htmlFor={field.name}>API Key</FormLabel>
                                        <FormControl>
                                            <Input 
                                                {...field} 
                                                type="password" 
                                                readOnly={readOnly}
                                                placeholder="sk-ant-..."
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="ANTHROPIC_MODEL"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel htmlFor={field.name}>Model</FormLabel>
                                        <FormControl>
                                            <Input 
                                                {...field} 
                                                readOnly={readOnly}
                                                placeholder="e.g., claude-sonnet-4-20250514, claude-3-5-sonnet-20241022"
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="ANTHROPIC_BASE_URL"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel htmlFor={field.name}>Base URL (optional)</FormLabel>
                                        <FormControl>
                                            <Input 
                                                {...field} 
                                                readOnly={readOnly}
                                                placeholder="https://api.anthropic.com"
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                    </div>
                    {submitButton ? (
                        submitButton
                    ) : hideSubmitButton ? null : (
                        <div className="flex justify-end pt-10">
                            <Button type="submit">Save Changes</Button>
                        </div>
                    )}
                </form>
            </Form>
        );
    }
);

AnthropicChatSettingsForm.displayName = "AnthropicChatSettingsForm";

export default AnthropicChatSettingsForm;

